import { useEffect, useRef } from 'react';
import { createNoise3D } from 'simplex-noise';
import { cn } from '../utils/cn';
import './Vortex.css';

const TAU = 2 * Math.PI;
const rand = (n) => n * Math.random();
const randRange = (n) => n - rand(2 * n);
const fadeInOut = (t, m) => {
  const hm = 0.5 * m;
  return Math.abs(((t + hm) % m) - hm) / hm;
};
const lerp = (n1, n2, speed) => (1 - speed) * n1 + speed * n2;

/**
 * React Vortex Background (shadcn-style canvas effect).
 * @param {object} props
 * @param {string} [props.className]
 * @param {import('react').ReactNode} [props.children]
 * @param {number} [props.particleCount]
 * @param {number} [props.rangeY]
 * @param {number} [props.baseHue]
 * @param {number} [props.baseSpeed]
 * @param {number} [props.rangeSpeed]
 * @param {number} [props.baseRadius]
 * @param {number} [props.rangeRadius]
 * @param {boolean} [props.transparentBackground] — clear frame instead of black so a CSS layer below shows through
 * @param {boolean} [props.fixed] — use fixed positioning (full viewport) instead of filling positioned parent
 */
export function Vortex({
  className,
  children,
  particleCount = 700,
  rangeY = 100,
  baseHue = 220,
  baseSpeed = 0,
  rangeSpeed = 1.5,
  baseRadius = 1,
  rangeRadius = 2,
  transparentBackground = false,
  fixed = false,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    let ctx;
    try {
      ctx = canvas.getContext('2d', { alpha: transparentBackground });
    } catch {
      return undefined;
    }
    if (!ctx) return undefined;

    const noise3D = createNoise3D();
    const particlePropCount = 9;
    const particlePropsLength = particleCount * particlePropCount;
    let particleProps = new Float32Array(particlePropsLength);
    let tick = 0;
    let width = 0;
    let height = 0;
    let _centerX = 0;
    let centerY = 0;

    const baseTTL = 50;
    const rangeTTL = 150;
    const rangeHue = 100;
    const noiseSteps = 3;
    const xOff = 0.00125;
    const yOff = 0.00125;
    const zOff = 0.0005;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = width;
      canvas.height = height;
      _centerX = width * 0.5;
      centerY = height * 0.5;
    };

    const initParticle = (i) => {
      const x = rand(width);
      const y = centerY + randRange(rangeY);
      const vx = 0;
      const vy = 0;
      const life = 0;
      const ttl = baseTTL + rand(rangeTTL);
      const speed = baseSpeed + rand(rangeSpeed);
      const radius = baseRadius + rand(rangeRadius);
      const hue = baseHue + rand(rangeHue);
      particleProps.set([x, y, vx, vy, life, ttl, speed, radius, hue], i);
    };

    const initParticles = () => {
      tick = 0;
      particleProps = new Float32Array(particlePropsLength);
      for (let i = 0; i < particlePropsLength; i += particlePropCount) {
        initParticle(i);
      }
    };

    const drawParticle = (x, y, x2, y2, life, ttl, radius, hue) => {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineWidth = radius;
      ctx.strokeStyle = `hsla(${hue},100%,60%,${fadeInOut(life, ttl)})`;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.restore();
    };

    const updateParticle = (i) => {
      const x = particleProps[i];
      const y = particleProps[i + 1];
      const n = noise3D(x * xOff, y * yOff, tick * zOff) * noiseSteps * TAU;
      const vx = lerp(particleProps[i + 2], Math.cos(n), 0.5);
      const vy = lerp(particleProps[i + 3], Math.sin(n), 0.5);
      let life = particleProps[i + 4];
      const ttl = particleProps[i + 5];
      const speed = particleProps[i + 6];
      const radius = particleProps[i + 7];
      const hue = particleProps[i + 8];

      const x2 = x + vx * speed;
      const y2 = y + vy * speed;

      drawParticle(x, y, x2, y2, life, ttl, radius, hue);

      life += 1;
      particleProps[i] = x2;
      particleProps[i + 1] = y2;
      particleProps[i + 2] = vx;
      particleProps[i + 3] = vy;
      particleProps[i + 4] = life;

      const outOfBounds = x2 > width || x2 < 0 || y2 > height || y2 < 0;
      if (outOfBounds || life > ttl) initParticle(i);
    };

    const renderGlow = () => {
      ctx.save();
      ctx.filter = 'blur(8px) brightness(200%)';
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();

      ctx.save();
      ctx.filter = 'blur(4px) brightness(200%)';
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();
    };

    const draw = () => {
      tick += 1;
      if (transparentBackground) {
        ctx.clearRect(0, 0, width, height);
      } else {
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, width, height);
      }

      for (let i = 0; i < particlePropsLength; i += particlePropCount) {
        updateParticle(i);
      }

      renderGlow();

      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();

      animationRef.current = requestAnimationFrame(draw);
    };

    resize();
    initParticles();
    animationRef.current = requestAnimationFrame(draw);

    const resizeObserver = new ResizeObserver(() => {
      resize();
      initParticles();
    });
    resizeObserver.observe(container);

    return () => {
      if (animationRef.current != null) cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
    };
  }, [
    particleCount,
    rangeY,
    baseHue,
    baseSpeed,
    rangeSpeed,
    baseRadius,
    rangeRadius,
    transparentBackground,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn('vortex-root', fixed && 'vortex-root--fixed', className)}
    >
      <canvas ref={canvasRef} className="vortex-canvas" />
      {children ? <div className="vortex-children">{children}</div> : null}
    </div>
  );
}

export default Vortex;
